<script setup lang="ts">
import { Alert, AlertDescription, AlertTitle } from '~ds/components/ui/alert';
import { Card } from '~ds/components/ui/card';
import { Skeleton } from '~ds/components/ui/skeleton';
import { Heading, Text } from '~ds/components/ui/typography';
import KUICorePagination from '~kui/components/KUI/Core/Pagination/KUICorePagination.vue';
import KUIUnifiedContentArticleCard from '~kui/components/KUI/Unified/Content/KUIUnifiedContentArticleCard.vue';
import KUIUnifiedContentArticleFeature from '~kui/components/KUI/Unified/Content/KUIUnifiedContentArticleFeature.vue';
import KUIUnifiedIndexHeaderIA from '~kui/components/KUI/Unified/Index/KUIUnifiedIndexHeaderIA.vue';

import { useArticleCardClick } from '~/composables/unified/useArticleCardClick';
import { useFeaturedMedia } from '~/composables/unified/useFeaturedMedia';
import { IaPageAudience } from '~/composables/unified/useIaAudience';
import { INDEX_ILLUSTRATIONS } from '~/constants/indexIllustrations';
import { parseGuidePage } from '~/utils/unified/institutionGuideQuery';

const PAGE_SIZE = 12;
const SKELETON_COUNT = 8;

definePageMeta({
  authType: [AUTH_ROUTE_TYPE_NONE],
  layout: 'unified-public',
  unavailableOnSchools: { navigateToLink: '/' },
  // Launches with the new information architecture; until then it sends visitors home.
  ia: { audience: IaPageAudience.All, off: '/' },
});

const { t } = useI18n();

// The spec's title already names Anyway, so the site's " | Anyway" suffix is left off.
useHead({ titleTemplate: '%s' });
useSeoMeta({
  title: t('unified.seo.mediaCentre.title'),
  description: t('unified.seo.mediaCentre.description'),
  ogTitle: t('unified.seo.mediaCentre.title'),
  ogDescription: t('unified.seo.mediaCentre.description'),
  ogType: 'website',
});

const { lead, articles, pending, error } = useFeaturedMedia();
const contactEmail = t('unified.mediaCentre.contactEmail');

// The lead tops page 1; the articles under it are paged. The page is mirrored in
// the query, so Back from an article returns to it, and is clamped to the pages there are.
const route = useRoute();
const router = useRouter();
const pageCount = computed(() => Math.max(1, Math.ceil(articles.value.length / PAGE_SIZE)));
const requestedPage = ref(parseGuidePage(route.query.page));
watch(() => route.query.page, (value) => {
  requestedPage.value = parseGuidePage(value);
});
const page = computed(() => Math.min(requestedPage.value, pageCount.value));
const pageArticles = computed(() => articles.value.slice((page.value - 1) * PAGE_SIZE, page.value * PAGE_SIZE));

// The URL follows the page shown, from this one place: a page change, a page past the end and an
// explicit page 1 all land here, so each page has one URL. It waits for the list, since an unloaded
// or failed list cannot say how many pages there are.
watch([page, pending, error], () => {
  const pageQuery = page.value > 1 ? String(page.value) : undefined;
  if (!pending.value && !error.value && route.query.page !== pageQuery) {
    router.replace({ query: { ...route.query, page: pageQuery } });
  }
}, { immediate: true });

// A page change goes back to the top of the page and moves focus to the page's first link (the
// lead's on page 1, the first card after), which the browser then scrolls into view if it is not.
const results = useTemplateRef<HTMLDivElement>('results');
const goToPage = async (value: number) => {
  requestedPage.value = value;
  await nextTick();
  window.scrollTo({ top: 0 });
  results.value?.querySelector<HTMLElement>('a')?.focus();
};

const trackCardClick = useArticleCardClick('media_centre');

const analytics = useAnalyticsService();
onMounted(() => {
  analytics.track(EVENTS.UNIFIED_PUBLIC_PAGE_VIEWED, { page: 'media_centre' });
});
</script>

<template>
  <div
    class="flex flex-col gap-10 py-10"
    data-testid="media-centre"
  >
    <KUIUnifiedIndexHeaderIA
      :title="t('unified.mediaCentre.title')"
      :subtitle="t('unified.mediaCentre.subtitle')"
      :illustration-src="`${INDEX_ILLUSTRATIONS}/media.webp`"
    />

    <div
      v-if="pending"
      class="flex flex-col gap-10"
      aria-busy="true"
      data-testid="media-centre-loading"
    >
      <Skeleton class="aspect-3/2 w-full rounded-card md:w-1/2" />
      <div class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        <Skeleton
          v-for="n in SKELETON_COUNT"
          :key="n"
          class="aspect-video rounded-card"
        />
      </div>
    </div>

    <Alert
      v-else-if="error"
      variant="error"
      data-testid="media-centre-error"
    >
      <AlertTitle>{{ t('unified.errors.genericFailed') }}</AlertTitle>
      <AlertDescription>{{ t('unified.mediaCentre.error') }}</AlertDescription>
    </Alert>

    <Text
      v-else-if="!lead"
      class="text-muted-foreground"
      data-testid="media-centre-empty"
    >
      {{ t('unified.mediaCentre.empty') }}
    </Text>

    <template v-else>
      <div
        ref="results"
        class="flex flex-col gap-10"
      >
        <KUIUnifiedContentArticleFeature
          v-if="page === 1"
          :article="lead"
          @select="trackCardClick(lead, 'lead', 0)"
        />

        <Heading
          v-if="pageArticles.length"
          id="media-centre-more"
          as="h2"
          class="sr-only"
        >
          {{ t('unified.mediaCentre.moreArticles') }}
        </Heading>
        <ul
          v-if="pageArticles.length"
          aria-labelledby="media-centre-more"
          class="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4"
          data-testid="media-centre-grid"
        >
          <li
            v-for="(article, index) in pageArticles"
            :key="article.id"
          >
            <KUIUnifiedContentArticleCard
              :article="article"
              size="fill"
              @click="trackCardClick(article, 'grid', (page - 1) * PAGE_SIZE + index)"
            />
          </li>
        </ul>
      </div>

      <KUICorePagination
        v-if="articles.length > PAGE_SIZE"
        :page="page"
        :total="articles.length"
        :items-per-page="PAGE_SIZE"
        class="self-center"
        data-testid="media-centre-pagination"
        @update:page="goToPage"
      />
    </template>

    <aside
      class="mx-auto mt-6"
      aria-labelledby="media-centre-contact-title"
      data-testid="media-centre-contact"
    >
      <Card class="flex-row items-center gap-4 px-8">
        <NuxtImg
          src="/images/kui/unified/anyway-logo-a.webp"
          alt=""
          width="45"
          height="45"
          class="size-11 shrink-0"
        />
        <div class="flex flex-col gap-1">
          <Heading
            id="media-centre-contact-title"
            as="h2"
            size="xs"
          >
            {{ t('unified.mediaCentre.contactTitle') }}
          </Heading>
          <Text size="sm">
            <i18n-t keypath="unified.mediaCentre.contactLine">
              <template #email>
                <a
                  :href="`mailto:${contactEmail}`"
                  class="underline"
                  data-testid="media-centre-email"
                >{{ contactEmail }}</a>
              </template>
            </i18n-t>
          </Text>
        </div>
      </Card>
    </aside>
  </div>
</template>
